import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = 'Asia/Kolkata';

/** Money arrives as a decimal string; INR with Indian grouping (PRD 5.6 Intl.NumberFormat en-IN). */
export function inr(v: string | number | null | undefined, forceDecimals = false): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v) || 0;
  const neg = n < 0;
  const s = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: forceDecimals ? 2 : n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
  return `${neg ? '−' : ''}₹${s}`;
}

/** ₹4.86 Cr / ₹93.80 L for headline figures. */
export function lakh(v: string | number | null | undefined): string {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
}

export const num = (v: string | number | null | undefined) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 }).format(Number(v) || 0);

/** 21 Oct 2026, from a date or timestamp. Date-only strings are not shifted by time zone. */
export function fmtDate(v: string | Date | null | undefined): string {
  if (!v) return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return dayjs(v).format('D MMM YYYY');
  return dayjs(v).tz(IST).format('D MMM YYYY');
}

/** 21 Oct 2026, 15:20 (IST). */
export function fmtDateTime(v: string | Date | null | undefined): string {
  if (!v) return '—';
  return dayjs(v).tz(IST).format('D MMM YYYY, HH:mm');
}

export function fmtShortDateTime(v: string | Date | null | undefined): string {
  if (!v) return '—';
  return dayjs(v).tz(IST).format('D MMM, HH:mm');
}

export const todayISO = () => dayjs().tz(IST).format('YYYY-MM-DD');
/** The Indian calendar day (YYYY-MM-DD) a timestamp falls on, the day the screens show it under. */
export const istDate = (v: string | Date) => dayjs(v).tz(IST).format('YYYY-MM-DD');
export const shiftDays = (iso: string, n: number) => dayjs(iso).add(n, 'day').format('YYYY-MM-DD');

export function relativeTime(v: string | Date | null | undefined): string {
  if (!v) return '';
  const mins = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h ago`;
  return fmtShortDateTime(v);
}

export const pct = (v: number | string | null | undefined, d = 0) => `${Number(v ?? 0).toFixed(d)}%`;

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter((c) => c && /[A-Za-z]/.test(c))
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const fileSize = (bytes: number) =>
  bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export const daysUntil = (iso: string) => dayjs(iso).startOf('day').diff(dayjs().tz(IST).startOf('day'), 'day');
